import { isEqual } from 'lodash';
import React from 'react';
import { ChevronDown } from 'react-feather';
import classNames from 'classnames';
import { pluralizeWithCount } from 'utils/string_utils';
import Popover from '../../popover';
import ScrollableContainer from '../../scrollable';
import ApplyButton from './apply_button';

const isArrayEqual = (arr1, arr2) => isEqual(arr1.sort(), arr2.sort());

class MultiSelectFilter extends React.Component {
  state = {
    isActive: false,
    lastSelectedOptions: this.props.selected || [],
    selectedOptions: this.props.selected || [],
  };

  static defaultProps = {
    wrapperClassName: 'IndexFilter IndexFilter-button',
    popoverClassName: 'IndexFilter-popover',
    label: { singular: 'option', plural: 'options' },
    sort: { key: 'name', descending: false },
    data: [], // you could pass an object with keys as ids as well
    selected: [], // selected identifiers (keys if data is object, indexes if data is an array)
    applyCallback: () => {}, // gets passed an array of selected identifiers
    whiteTheme: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const nextSelected = nextProps.selected || [];
    if (!isArrayEqual(nextSelected, this.state.selectedOptions)) {
      this.setState({
        selectedOptions: nextSelected,
        lastSelectedOptions: nextSelected,
      });
    }
  }

  getLabel() {
    const count = this.state.selectedOptions.length;
    const { singular, plural } = this.props.label;
    return count
      ? `${pluralizeWithCount(count, singular, plural)} selected`
      : plural;
  }

  getSortedKeys() {
    const order = this.props.sort.descending ? -1 : 1;
    return Object.keys(this.props.data)
      .map(keyName => {
        let value = this.props.data[keyName][this.props.sort.key];
        value = typeof value === 'string' ? value.toLowerCase() : value;
        return [parseInt(keyName, 10), value];
      })
      .sort((current, next) => (current[1] < next[1] ? order * -1 : order));
  }

  get shouldApplyFilter() {
    const { selectedOptions, lastSelectedOptions } = this.state;
    return !isArrayEqual(selectedOptions, lastSelectedOptions);
  }

  toggleMenu = () => {
    this.setState(state => ({ isActive: !state.isActive }));
  };

  handleOptionClick = key => {
    const selectedOptions = new Set(this.state.selectedOptions);
    if (selectedOptions.has(key)) {
      selectedOptions.delete(key);
    } else {
      selectedOptions.add(key);
    }
    this.setState({ selectedOptions: Array.from(selectedOptions) });
  };

  applyChanges = () => {
    if (this.shouldApplyFilter) {
      const { selectedOptions } = this.state;
      const { applyCallback } = this.props;
      this.setState({
        isActive: false,
        lastSelectedOptions: selectedOptions,
      });
      applyCallback(selectedOptions);
    }
  };

  renderToggleButton() {
    const { isActive } = this.state;
    const {
      renderToggle: customToggle,
      whiteTheme,
      accessible = false,
      featherIcon = false,
    } = this.props;
    if (customToggle) {
      return customToggle({ isActive, toggle: this.toggleMenu });
    }
    const className = classNames({
      NewButton: !accessible,
      AccessibleSelect: accessible,
      'NewButton--active': isActive,
      'NewButton--white': whiteTheme,
      'NewButton--darkNavy': !whiteTheme,
      'd-f jc-sb ai-c pr-4': featherIcon,
    });
    return (
      <button
        type="button"
        className={className}
        onMouseDown={e => {
          // Prevents mouse click focus ring
          e.preventDefault();
        }}
        onClick={this.toggleMenu}
      >
        {this.getLabel()}
        {featherIcon && <ChevronDown className="" size={18} />}
      </button>
    );
  }

  render() {
    const { wrapperClassName, popoverClassName } = this.props;
    return (
      <div className={wrapperClassName}>
        {this.renderToggleButton()}
        {this.state.isActive && (
          <Popover
            styleName={popoverClassName}
            dismissCallback={this.toggleMenu}
          >
            <ScrollableContainer>
              {this.getSortedKeys().map(key => (
                <label
                  htmlFor={`select-option-${key[0]}`}
                  key={key[0]}
                  className="NewPopoverOption"
                >
                  <input
                    id={`select-option-${key[0]}`}
                    checked={this.state.selectedOptions.indexOf(key[0]) > -1}
                    onChange={() => this.handleOptionClick(key[0])}
                    type="checkbox"
                    onKeyDown={e =>
                      e.key === 'Enter' && this.handleOptionClick(key[0])
                    }
                  />
                  <span className="NewPopoverOption-label">
                    {this.props.data[key[0]].name}
                  </span>
                  <span className="NewPopoverOption-count">
                    {this.props.data[key[0]].count}
                  </span>
                </label>
              ))}
            </ScrollableContainer>
            <ApplyButton
              onClick={this.applyChanges}
              enabled={this.shouldApplyFilter}
              accessible
            />
          </Popover>
        )}
      </div>
    );
  }
}

export default MultiSelectFilter;
